/**
 * File type detection and helper utilities
 */

const VIDEO_EXTENSIONS = ['mp4', 'mpeg', 'mpg', 'mov', 'avi', 'webm', 'flv', 'mkv', 'ogg', 'ogv'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'oga', 'aac', 'flac', 'm4a', 'wma'];

/**
 * Extract file extension from filename
 */
export const getFileExtension = (fileName: string): string => {
    return fileName.split('.').pop()?.toLowerCase() || '';
};

/**
 * Check if file is a video
 */
export const isVideoFile = (mimetype?: string, fileName?: string): boolean => {
    const extension = fileName ? getFileExtension(fileName) : '';
    return mimetype?.includes('video') || VIDEO_EXTENSIONS.includes(extension);
};

/**
 * Check if file is audio
 */
export const isAudioFile = (mimetype?: string, fileName?: string): boolean => {
    const extension = fileName ? getFileExtension(fileName) : '';
    return mimetype?.includes('audio') || AUDIO_EXTENSIONS.includes(extension);
};

/**
 * Check if file is an image
 */
export const isImageFile = (mimetype?: string, fileName?: string): boolean => {
    const extension = fileName ? getFileExtension(fileName) : '';
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    return mimetype?.includes('image') || imageExtensions.includes(extension);
};

/**
 * Check if file is a PDF
 */
export const isPdfFile = (mimetype?: string, fileName?: string): boolean => {
    const extension = fileName ? getFileExtension(fileName) : '';
    return mimetype?.includes('pdf') || extension === 'pdf';
};

/**
 * Get appropriate icon class for file type
 */
export const getFileIcon = (mimetype?: string, fileName?: string): string => {
    const extension = fileName ? getFileExtension(fileName) : '';

    if (isPdfFile(mimetype, fileName)) {
        return 'fi fi-br-file-pdf';
    }
    if (isVideoFile(mimetype, fileName)) {
        return 'fi fi-br-file-video';
    }
    if (isAudioFile(mimetype, fileName)) {
        return 'fi fi-br-file-music';
    }
    if (isImageFile(mimetype, fileName)) {
        return 'fi fi-br-file-image';
    }
    if (mimetype?.includes('word') || ['doc', 'docx'].includes(extension)) {
        return 'fi fi-br-file-word';
    }
    if (mimetype?.includes('excel') || mimetype?.includes('spreadsheet') || ['xls', 'xlsx'].includes(extension)) {
        return 'fi fi-br-file-excel';
    }
    if (mimetype?.includes('powerpoint') || mimetype?.includes('presentation') || ['ppt', 'pptx'].includes(extension)) {
        return 'fi fi-br-file-powerpoint';
    }
    if (mimetype?.includes('zip') || mimetype?.includes('compressed') || ['zip', 'rar', '7z'].includes(extension)) {
        return 'fi fi-br-zip-file';
    }

    return 'fi fi-br-file';
};

/**
 * Get appropriate color icon for file type
 */
export const getFileIconColor = (mimetype?: string, fileName?: string): string => {
    if (isPdfFile(mimetype, fileName)) {
        return '#E9A0AC';
    }
    if (isVideoFile(mimetype, fileName)) {
        return '#FF9900';
    }
    if (isAudioFile(mimetype, fileName)) {
        return '#FF6600';
    }
    if (isImageFile(mimetype, fileName)) {
        return '#3399FF';
    }
    if (mimetype?.includes('word') || fileName?.toLowerCase().endsWith('.docx') || fileName?.toLowerCase().endsWith('.doc')) {
        return '#A0C6E9';
    }
    if (mimetype?.includes('excel') || mimetype?.includes('spreadsheet') || fileName?.toLowerCase().endsWith('.xlsx') || fileName?.toLowerCase().endsWith('.xls')) {
        return '#CEE9A0';
    }
    if (mimetype?.includes('powerpoint') || mimetype?.includes('presentation') || fileName?.toLowerCase().endsWith('.pptx') || fileName?.toLowerCase().endsWith('.ppt')) {
        return '#E9C6A0';
    }
    if (mimetype?.includes('zip') || mimetype?.includes('compressed') || fileName?.toLowerCase().endsWith('.zip') || fileName?.toLowerCase().endsWith('.rar') || fileName?.toLowerCase().endsWith('.7z')) {
        return '#B0A0E9';
    }
    return '#AAAAAA';
}

/**
 * Check if file can be previewed in the browser
 */
export const canPreviewFile = (mimetype?: string, fileName?: string): boolean => {
    return (
        isImageFile(mimetype, fileName) ||
        isPdfFile(mimetype, fileName) ||
        isVideoFile(mimetype, fileName) ||
        isAudioFile(mimetype, fileName)
    );
};
